import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Bell, Repeat, GraduationCap, Building2, School, Users, ChevronDown } from "lucide-react";

const ROLE_META = {
  student: { label: "Student", icon: GraduationCap, dest: "/dashboard" },
  partner: { label: "Partner", icon: Building2, dest: "/partner/dashboard" },
  teacher: { label: "Instructor", icon: School, dest: "/teacher/dashboard" },
  parent:  { label: "Parent", icon: Users, dest: "/parent/dashboard" },
  admin:   { label: "Admin", icon: Building2, dest: "/admin" },
};

/** Dropdown that lets any user with >1 role flip between dashboards
 *  without signing out. Also handy for admins for QA / support. */
export function RoleSwitcher() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!user) return null;
  const owned = Array.from(new Set([...(user.roles || []), user.role])).filter(Boolean);
  if (owned.length <= 1 && user.role !== "admin") return null;

  const current = ROLE_META[user.role] || { label: user.role, icon: GraduationCap };
  const Icon = current.icon;

  const flip = async (r) => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/switch-role", { role: r });
      localStorage.setItem("tmo_token", data.access_token);
      localStorage.setItem("tmo_user", JSON.stringify(data.user));
      setOpen(false);
      nav(ROLE_META[r]?.dest || "/");
      // hard refresh so AuthContext re-reads user
      window.location.reload();
    } catch (e) {
      // silently fail — user still on same role
    } finally { setBusy(false); }
  };

  const options = user.role === "admin"
    ? ["admin", "partner", "teacher", "parent", "student"]
    : owned;

  return (
    <div ref={ref} className="relative" data-testid="role-switcher">
      <button onClick={() => setOpen(!open)}
        data-testid="role-switcher-toggle"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E7EB] text-xs font-semibold hover:border-[#DC2626] transition-colors">
        <Icon className="w-3.5 h-3.5" />
        {current.label}
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white border border-[#E5E7EB] rounded-lg shadow-xl overflow-hidden z-50"
          data-testid="role-switcher-menu">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-gray-500 border-b">Switch dashboard</div>
          {options.map((r) => {
            const meta = ROLE_META[r] || { label: r, icon: Repeat };
            const RI = meta.icon;
            const isCurrent = r === user.role;
            return (
              <button key={r}
                data-testid={`role-switcher-option-${r}`}
                disabled={busy || isCurrent}
                onClick={() => flip(r)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                  isCurrent ? "bg-[#F0FDF4] text-[#065F46] font-semibold cursor-default"
                            : "hover:bg-gray-50"}`}>
                <RI className="w-4 h-4" />
                {meta.label}
                {isCurrent && <span className="ml-auto text-[10px]">CURRENT</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Admin-only bell showing counts of things awaiting review. */
export function AdminNotificationBell() {
  const { user } = useAuth();
  const [data, setData] = useState({ total: 0, items: [] });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const load = () => api.get("/api/admin/notifications")
      .then(({ data }) => setData(data)).catch(() => {});
    load();
    const t = setInterval(load, 45_000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (user?.role !== "admin") return null;

  return (
    <div ref={ref} className="relative" data-testid="admin-notification-bell">
      <button onClick={() => setOpen(!open)}
        data-testid="admin-notification-toggle"
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors">
        <Bell className="w-5 h-5 text-[#111827]" />
        {data.total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-[#DC2626] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 grid place-items-center"
            data-testid="admin-notification-badge">
            {data.total > 99 ? "99+" : data.total}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-[#E5E7EB] rounded-lg shadow-xl overflow-hidden z-50"
          data-testid="admin-notification-menu">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="font-bold text-sm">Admin inbox</div>
            <span className="text-[10px] text-gray-500">Live · updates every 45s</span>
          </div>
          {data.items.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500"
              data-testid="admin-notification-empty">
              🎉 All caught up — nothing waiting.
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {data.items.map((it) => (
                <Link key={it.kind} to={it.href}
                  data-testid={`admin-notification-item-${it.kind}`}
                  onClick={() => setOpen(false)}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 border-b last:border-b-0">
                  <div className="w-8 h-8 rounded-lg bg-[#FEE2E2] text-[#DC2626] grid place-items-center font-bold text-sm shrink-0">
                    {it.count}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold leading-tight">{it.label}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">Tap to open →</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
